import atlas from 'azure-maps-control';
import proj4 from 'proj4';

// Helper function to round coordinates to 6 decimal places
// const roundCoord = (value: number) => parseFloat(value.toFixed(6));

// Use higher precision rounding without `toFixed()`
const preciseRound = (value: number, decimals = 10) => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

export class MapService {

  // Private static instance of the class (singleton)
  private static instance: MapService;
  private mapInstance: atlas.Map | null = null;

  // Private constructor to prevent direct instantiation
  private constructor() {
    proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
    proj4.defs("EPSG:28992", "+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel +towgs84=565.4171,50.3319,465.5524,-0.398957,0.343988,-1.8774,4.0725 +units=m +no_defs");
  }

  // Private constructor to prevent direct instantiation
  public setMapInstance(map: atlas.Map | null = null) {
    this.mapInstance = map;
  }

  public getMapInstance() {
    if (!this.mapInstance) {
        console.warn("Map instance not initialized yet.");
    }
    return this.mapInstance;
  }

  // Method to return the singleton instance
  public static getInstance(): MapService {
    if (!MapService.instance) {
      MapService.instance = new MapService();
    }
    return MapService.instance;
  }

  public isValidBbox(bbox: unknown): bbox is [number, number, number, number] {
    return Array.isArray(bbox)
      && bbox.length === 4
      && bbox.every(value => typeof value === 'number' && Number.isFinite(value));
  }

  // Function to calculate centroid of a polygon
  public calculateCentroid(coordinates: number[][] | number[][][]): number[] {
    // Ensure we are getting the first element if coordinates are deeply nested
    if (Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0])) {
      coordinates = coordinates[0] as number[][];
    }

    let xSum = 0, ySum = 0, numPoints = coordinates.length;

    coordinates.forEach(([x, y]) => {
      xSum += Number(x);
      ySum += Number(y);
    });

    return [xSum / numPoints, ySum / numPoints];
  }

  // public calculatedBoundingBox = (bboxes: number[][]) => {
  //   if (!bboxes.length) return null;

  //   const min_X = Math.min(...bboxes.map(bbox => bbox[0])); // min Lng / west
  //   const min_Y = Math.min(...bboxes.map(bbox => bbox[1])); // min Lat / south
  //   const max_X = Math.max(...bboxes.map(bbox => bbox[2])); // max Lng / east
  //   const max_Y = Math.max(...bboxes.map(bbox => bbox[3])); // max Lat / north

  //   return [min_X, min_Y, max_X, max_Y];
  // };

  public calculatedBoundingBox = (bboxes: number[][]) => {
    // const map = mapService.getMapInstance();
    
    if (!bboxes.length) return null;

    // Compute min/max bounds from data
    const min_X = Math.min(...bboxes.map(bbox => bbox[0])); // min Lng / west
    const min_Y = Math.min(...bboxes.map(bbox => bbox[1])); // min Lat / south
    const max_X = Math.max(...bboxes.map(bbox => bbox[2])); // max Lng / east
    const max_Y = Math.max(...bboxes.map(bbox => bbox[3])); // max Lat / north

    let bbox = [min_X, min_Y, max_X, max_Y];

    if (this.mapInstance) {
        // const camera = this.mapInstance.getCamera();
        // const mapWidth = camera.width;
        // const mapHeight = camera.height;
        const mapCanvas = this.mapInstance.getCanvas();
        const mapWidth = mapCanvas.clientWidth; // ✅ Correct way to get width
        const mapHeight = mapCanvas.clientHeight; // ✅ Correct way to get height

        if (mapWidth && mapHeight) {
            const aspectRatio = mapWidth / mapHeight;
            bbox = this.expandBBoxToFitAspectRatio(bbox, aspectRatio);
        }
    }

    return bbox;
  };

  private expandBBoxToFitAspectRatio = (bbox: number[], aspectRatio: number) => {
    const [min_X, min_Y, max_X, max_Y] = bbox;

    // Calculate width & height of current bbox
    const bboxWidth = max_X - min_X;
    const bboxHeight = max_Y - min_Y;
    const bboxAspectRatio = bboxWidth / bboxHeight;

    let newMin_X = min_X, newMax_X = max_X;
    let newMin_Y = min_Y, newMax_Y = max_Y;

    // Adjust the bbox to fit the map's aspect ratio
    if (bboxAspectRatio < aspectRatio) {
        // Expand width to match aspect ratio
        const newWidth = bboxHeight * aspectRatio;
        const diff = (newWidth - bboxWidth) / 2;
        newMin_X -= diff;
        newMax_X += diff;
    } 
    else {
        // Expand height to match aspect ratio
        const newHeight = bboxWidth / aspectRatio;
        const diff = (newHeight - bboxHeight) / 2;
        newMin_Y -= diff;
        newMax_Y += diff;
    }

    return [newMin_X, newMin_Y, newMax_X, newMax_Y];
  };

  public convertBboxLatLngToRD(bbox: number[], returnAsArray: boolean = false) {
    const [min_X, min_Y] = proj4("EPSG:4326", "EPSG:28992", [bbox[0], bbox[1]]);
    const [max_X, max_Y] = proj4("EPSG:4326", "EPSG:28992", [bbox[2], bbox[3]]);

    const roundedBBox = [
        preciseRound(min_X, 10), 
        preciseRound(min_Y, 10), 
        preciseRound(max_X, 10), 
        preciseRound(max_Y, 10)
    ];

    return returnAsArray ? roundedBBox : roundedBBox.join(",");
  }

  public convertBboxRDToLatLng(bbox: number[], returnAsArray: boolean = false) {
    const [min_X, min_Y] = proj4("EPSG:28992", "EPSG:4326", [bbox[0], bbox[1]]);
    const [max_X, max_Y] = proj4("EPSG:28992", "EPSG:4326", [bbox[2], bbox[3]]);

    const roundedBBox = [
        preciseRound(min_X, 10), 
        preciseRound(min_Y, 10), 
        preciseRound(max_X, 10), 
        preciseRound(max_Y, 10)
    ];

    return returnAsArray ? roundedBBox : roundedBBox.join(",");
  }


  // public convertBboxLatLngToRD(bbox: number[], returnAsArray: boolean = false) {
  //   const [min_X, min_Y] = proj4("EPSG:4326", "EPSG:28992", [bbox[0], bbox[1]]); // west, south
  //   const [max_X, max_Y] = proj4("EPSG:4326", "EPSG:28992", [bbox[2], bbox[3]]); // east, north

  //   if (returnAsArray) {
  //     return [min_X, min_Y, max_X, max_Y];
  //   }

  //   return `${min_X},${min_Y},${max_X},${max_Y}`;
  // }

  // public convertBboxRDToLatLng(bbox: number[], returnAsArray: boolean = false) {
  //   const [min_X, min_Y] = proj4("EPSG:28992", "EPSG:4326", [bbox[0], bbox[1]]);
  //   const [max_X, max_Y] = proj4("EPSG:28992", "EPSG:4326", [bbox[2], bbox[3]]);

  //   if (returnAsArray) {
  //     return [min_X, min_Y, max_X, max_Y];
  //   }

  //   return `${min_X},${min_Y},${max_X},${max_Y}`;
  // }

  public convertRDToWGS84_OLD(coordinates: any) {
      if (!Array.isArray(coordinates)) {
        console.warn("Invalid input: coordinates is not an array", coordinates);
        return [];
      }

      return coordinates.map((polygonOrRing) => {
        if (!Array.isArray(polygonOrRing)) {
          console.warn("Invalid input: polygonOrRing is not an array", polygonOrRing);
          return [];
        }

        return polygonOrRing.map((ringOrPoint) => {
          if (!Array.isArray(ringOrPoint) || ringOrPoint.length === 0) {
            console.warn("Invalid input: ringOrPoint is not an array or empty", ringOrPoint);
            return [];
          }

          return Array.isArray(ringOrPoint[0]) // Detect if it's a polygon ring or a single point
            ? ringOrPoint.map(([x, y]) => {
                if (typeof x !== "number" || typeof y !== "number") {
                  console.warn("Invalid coordinates:", x, y);
                  return [0, 0]; // Default value or handle accordingly
                }
                return proj4("EPSG:28992", "EPSG:4326", [x, y]);
              })
            : proj4("EPSG:28992", "EPSG:4326", ringOrPoint);
        });
      });
  };

  public convertRDToWGS84(coordinates: any): any {
    if (!Array.isArray(coordinates)) {
      console.warn("Invalid input: coordinates is not an array", coordinates);
      return [];
    }

    // Leaf: single coordinate pair [x, y]
    if (coordinates.length >= 2 && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
      return proj4("EPSG:28992", "EPSG:4326", coordinates);
    }

    // Recurse into nested arrays (rings, polygons, multipolygons, flat point lists, ...)
    return coordinates.map((item: any) => this.convertRDToWGS84(item));
  }
};

// Export an instance of the mapService class
export const mapService = MapService.getInstance();